// Settings > Account editors: receipt email, add a password to a Google
// account, and change an existing password. Each is a single-purpose sheet.
import { useEffect, useState } from "react";
import accountService from "../../Services/accountService";
import authService from "../../Services/authService";
import { authErrorMessage } from "../../Services/authErrors";
import BottomSheet from "../BottomSheet";
import { TextField, PasswordField, PrimaryButton, FormError, LinkButton } from "../auth/AuthFields";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ReceiptEmailSheet({ isOpen, onClose, savedEmail, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setValue(savedEmail || "");
      setFieldError("");
      setError("");
    }
  }, [isOpen, savedEmail]);

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return setFieldError("Enter an email address.");
    if (!EMAIL_RE.test(trimmed)) return setFieldError("That email doesn't look right.");

    setSaving(true);
    setFieldError("");
    setError("");
    try {
      await accountService.saveEmail(trimmed);
      onSaved(trimmed);
      onClose();
    } catch (err) {
      console.error("Failed to save email:", err);
      setError("We couldn't save your email. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError("");
    try {
      await accountService.removeEmail();
      onSaved("");
      onClose();
    } catch (err) {
      console.error("Failed to remove email:", err);
      setError("We couldn't remove your email. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={saving ? () => {} : onClose} title="Receipt email" subtitle="Where we send purchase receipts">
      <form onSubmit={handleSave} noValidate className="space-y-4">
        <TextField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          error={fieldError}
          placeholder="you@example.com"
        />
        <FormError>{error}</FormError>
        <PrimaryButton loading={saving} loadingLabel="Saving…">
          Save
        </PrimaryButton>
        {savedEmail && (
          <div className="text-center">
            <LinkButton onClick={handleRemove} disabled={saving}>
              Remove saved email
            </LinkButton>
          </div>
        )}
      </form>
    </BottomSheet>
  );
}

export function AddPasswordSheet({ isOpen, onClose, onLinked }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setPassword("");
      setFieldErrors({});
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!email.trim()) errors.email = "Enter your email.";
    else if (!EMAIL_RE.test(email.trim())) errors.email = "That email doesn't look right.";
    if (!password) errors.password = "Choose a password.";
    else if (password.length < 6) errors.password = "Use at least 6 characters.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError("");
    try {
      const result = await authService.linkEmailPasswordToCurrentUser(email.trim(), password);
      if (result.success) {
        onLinked();
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Link email/password error:", err);
      setError(authErrorMessage(err, "We couldn't add your password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      title="Add a password"
      subtitle="Also sign in with your email and a password"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <TextField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          error={fieldErrors.email}
          placeholder="you@example.com"
        />
        <PasswordField
          label="Password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          error={fieldErrors.password}
          hint="At least 6 characters"
          placeholder="Create a password"
        />
        <FormError>{error}</FormError>
        <PrimaryButton loading={loading} loadingLabel="Adding…">
          Add password
        </PrimaryButton>
      </form>
    </BottomSheet>
  );
}

export function ChangePasswordSheet({ isOpen, onClose, onChanged }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setConfirm("");
      setFieldErrors({});
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!password) errors.password = "Enter a new password.";
    else if (password.length < 6) errors.password = "Use at least 6 characters.";
    if (!confirm) errors.confirm = "Type the new password again.";
    else if (password && confirm !== password) errors.confirm = "The passwords don't match.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError("");
    try {
      const result = await authService.updatePassword(password);
      if (result.success) {
        onChanged();
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Change password error:", err);
      setError(authErrorMessage(err, "We couldn't change your password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={loading ? () => {} : onClose} title="Change password" subtitle="Choose a new password for your account">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <PasswordField
          label="New password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          error={fieldErrors.password}
          hint="At least 6 characters"
          placeholder="New password"
        />
        <PasswordField
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={loading}
          error={fieldErrors.confirm}
          placeholder="Type it again"
        />
        <FormError>{error}</FormError>
        <PrimaryButton loading={loading} loadingLabel="Changing…">
          Change password
        </PrimaryButton>
      </form>
    </BottomSheet>
  );
}
